/**
 * Fill-ins a sender completes when sending a letter from a claim, stored as
 * the template's `prompts`. Each is used in the subject or message as
 * `{{ prompts.<key> }}`; the composer asks for it and the send fills it in.
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Trash2 } from 'lucide-react'

import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui/select'

import { promptToken } from './prompt-token'
import { slugify } from './slug'
import type { PromptDefinition } from './types'

export function PromptsEditor({
  value,
  onChange,
  lockedKeys,
  disabled = false,
}: {
  value: PromptDefinition[]
  onChange: (next: PromptDefinition[]) => void
  /** Keys already saved: the body may use them, so they never change. */
  lockedKeys: Set<string>
  disabled?: boolean
}) {
  const { t } = useTranslation()
  // The choices as typed, so a trailing comma survives until the next choice.
  const [optionDrafts, setOptionDrafts] = useState<Record<number, string>>({})
  const counts = value.reduce<Record<string, number>>((acc, prompt) => {
    acc[prompt.key] = (acc[prompt.key] ?? 0) + 1
    return acc
  }, {})

  function patch(index: number, next: Partial<PromptDefinition>) {
    onChange(value.map((prompt, i) => (i === index ? { ...prompt, ...next } : prompt)))
  }

  function rename(index: number, label: string) {
    const prompt = value[index]
    const next: Partial<PromptDefinition> = { label }
    // A new fill-in's key follows its label until the template is saved.
    if (!lockedKeys.has(prompt.key)) next.key = slugify(label)
    patch(index, next)
  }

  return (
    <div className="flex flex-col gap-3">
      {value.length === 0 && (
        <p className="text-xs text-slate-400">
          {t('emailTemplates.fillIns.empty', 'No fill-ins yet.')}
        </p>
      )}
      {value.map((prompt, index) => (
        <div
          key={index}
          className="grid grid-cols-1 gap-3 rounded-lg border border-slate-200 p-3 md:grid-cols-[1fr_160px]"
        >
          <div>
            <Label>{t('emailTemplates.fillIns.label', 'Label')}</Label>
            <Input
              value={prompt.label}
              onChange={(event) => rename(index, event.target.value)}
              placeholder={t('emailTemplates.fillIns.labelPlaceholder', 'e.g. Concern')}
              disabled={disabled}
            />
          </div>
          <div>
            <Label>{t('emailTemplates.fillIns.type', 'Type')}</Label>
            <Select
              value={prompt.type}
              onValueChange={(next: string) =>
                patch(index, {
                  type: next as PromptDefinition['type'],
                  options: next === 'enum' ? (prompt.options ?? []) : undefined,
                })
              }
              disabled={disabled}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="text">{t('emailTemplates.fillIns.typeText', 'Text')}</SelectItem>
                <SelectItem value="number">
                  {t('emailTemplates.fillIns.typeNumber', 'Number')}
                </SelectItem>
                <SelectItem value="enum">
                  {t('emailTemplates.fillIns.typeChoice', 'Choice from a list')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          {prompt.type === 'enum' && (
            <div className="md:col-span-2">
              <Label>{t('emailTemplates.fillIns.options', 'Choices, separated by commas')}</Label>
              <Input
                value={optionDrafts[index] ?? (prompt.options ?? []).join(', ')}
                onChange={(event) => {
                  const raw = event.target.value
                  setOptionDrafts((prev) => ({ ...prev, [index]: raw }))
                  patch(index, {
                    options: raw
                      .split(',')
                      .map((option) => option.trim())
                      .filter(Boolean),
                  })
                }}
                onBlur={() =>
                  setOptionDrafts((prev) => {
                    const next = { ...prev }
                    delete next[index]
                    return next
                  })
                }
                placeholder={t('emailTemplates.fillIns.optionsPlaceholder', 'e.g. Small, Medium, Large')}
                disabled={disabled}
              />
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-3 md:col-span-2">
            <Label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
              <Checkbox
                checked={!!prompt.required}
                onCheckedChange={(checked) => patch(index, { required: checked === true })}
                disabled={disabled}
              />
              {t('emailTemplates.fillIns.required', 'Required before sending')}
            </Label>
            <div className="flex items-center gap-2">
              {prompt.key && (
                <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600">
                  {promptToken(prompt.key)}
                </code>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => {
                  setOptionDrafts({})
                  onChange(value.filter((_, i) => i !== index))
                }}
                disabled={disabled}
                aria-label={t('emailTemplates.fillIns.remove', 'Remove fill-in')}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          </div>
          {prompt.key && counts[prompt.key] > 1 && (
            <p className="text-xs text-[var(--status-error)] md:col-span-2">
              {t('emailTemplates.fillIns.duplicate', 'Two fill-ins share this label. Give each a different one.')}
            </p>
          )}
        </div>
      ))}
      {value.some((prompt) => prompt.required) && (
        <p className="text-[11px] text-[var(--ink-3)]" data-testid="fill-ins-trigger-note">
          {t(
            'emailTemplates.fillIns.triggerNote',
            'A trigger will not send this letter while a fill-in is required. Send it from the claim instead.',
          )}
        </p>
      )}
      {!disabled && (
        <div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onChange([...value, { key: '', label: '', type: 'text', required: true }])}
          >
            <Plus size={14} />
            {t('emailTemplates.fillIns.add', 'Add fill-in')}
          </Button>
        </div>
      )}
    </div>
  )
}
